import React, { useCallback, useEffect, useState } from 'react'
import { getAuthUser, db } from '../../firebase'
import ProfileLayout from './ProfileLayout'
import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import Spinner from '../util/Spinner'
import ModalWrapper from '../modals/ModalWrapper'

const API_BASE_URL = `${window.location.origin}/api-v1`

function bufferToHex (buffer) {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function bufferToBase64Url (buffer) {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  for (let i = 0; i < bytes.byteLength; i += 1) binary += String.fromCharCode(bytes[i])
  return window.btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

async function generateToken () {
  const bytes = new Uint8Array(32)
  window.crypto.getRandomValues(bytes)
  const token = `dm_${bufferToBase64Url(bytes)}`
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(token))
  const hash = bufferToHex(hashBuffer)
  return { token, hash }
}

function formatDate (value) {
  if (!value) return null
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleString()
}

export default function ApiTokens () {
  const [user, setUser] = useState(null)
  const [tokenMeta, setTokenMeta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [generating, setGenerating] = useState(false)
  const [copied, setCopied] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState(false)

  const loadToken = useCallback(async () => {
    const currentUser = await getAuthUser()
    setUser(currentUser)
    if (!currentUser) {
      setLoading(false)
      return
    }
    const snap = await getDoc(doc(db, 'apiTokens', currentUser.uid))
    setTokenMeta(snap.exists() ? snap.data() : null)
    setLoading(false)
  }, [])

  useEffect(() => {
    loadToken()
  }, [loadToken])

  const handleGenerate = async () => {
    setGenerating(true)
    setCopied(false)
    try {
      const currentUser = await getAuthUser()
      const { token, hash } = await generateToken()
      const prefix = token.slice(0, 11)
      await setDoc(doc(db, 'apiTokens', currentUser.uid), {
        token,
        hash,
        prefix,
        createdAt: serverTimestamp(),
      })
      setTokenMeta({ token, hash, prefix, createdAt: new Date() })
    } finally {
      setGenerating(false)
      setConfirmRegenerate(false)
    }
  }

  const handleRevoke = async () => {
    const currentUser = await getAuthUser()
    await deleteDoc(doc(db, 'apiTokens', currentUser.uid))
    setTokenMeta(null)
    setConfirmRevoke(false)
  }

  const handleCopy = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (e) {
      setCopied(false)
    }
  }

  return (
    <ProfileLayout title="API">
      <div className='flex justify-between items-center gap-4 mb-4'>
        <h2 className='font-semibold text-2xl'>API access</h2>
      </div>
      <p className='text-sm text-gray-400 mb-6'>
        Use an API token to manage your devices and dashboards programmatically.
      </p>

      {loading && (
        <div className='w-8 my-8'>
          <Spinner />
        </div>
      )}

      {!loading && user && (
        <>
          <div className='p-4 bg-gray-800 rounded'>
            {tokenMeta ? (
              <div>
                <div className='mb-2'>
                  <span className='font-semibold text-xs text-gray-500'>Active token</span>
                  {formatDate(tokenMeta.createdAt) && (
                    <span className='text-xs text-gray-500 ml-2'>· Created {formatDate(tokenMeta.createdAt)}</span>
                  )}
                </div>
                <button
                  type='button'
                  onClick={() => handleCopy(tokenMeta.token)}
                  className='w-full text-left px-3 py-2 bg-gray-900 rounded text-sm font-mono break-all hover:bg-gray-950 transition-colors flex items-center justify-between gap-3'
                  title='Click to copy'
                >
                  <span>{revealed ? tokenMeta.token : `${tokenMeta.prefix}${'•'.repeat(32)}`}</span>
                  <span className='text-xs text-gray-500 font-sans shrink-0'>
                    {copied ? 'Copied ✓' : 'Click to copy'}
                  </span>
                </button>
                <div className='mt-3 text-xs flex items-center gap-4'>
                  <button
                    type='button'
                    onClick={() => setRevealed((v) => !v)}
                    className='text-gray-400 hover:text-gray-200'
                  >
                    {revealed ? 'Hide' : 'Reveal'}
                  </button>
                  <div className='ml-auto flex gap-4'>
                    <button
                      type='button'
                      onClick={() => setConfirmRegenerate(true)}
                      disabled={generating}
                      className='text-gray-400 hover:text-gray-200 disabled:opacity-50'
                    >
                      Regenerate
                    </button>
                    <button
                      type='button'
                      onClick={() => setConfirmRevoke(true)}
                      className='text-red-400 hover:text-red-300'
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className='flex items-center justify-between gap-4 flex-wrap'>
                <div className='text-sm text-gray-300'>You don't have an API token yet.</div>
                <button
                  className='px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-700 rounded text-white'
                  onClick={handleGenerate}
                  disabled={generating}
                >
                  {generating ? 'Generating...' : 'Generate API token'}
                </button>
              </div>
            )}
          </div>

          <div className='mt-8'>
            <div className='flex items-center justify-between gap-4 flex-wrap mb-2'>
              <h3 className='font-semibold text-lg'>API reference</h3>
              <span className='flex-1'></span>
              <a
                href={`${API_BASE_URL}/docs`}
                target='_blank'
                rel='noreferrer'
                className='px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 rounded'
              >
                API Reference ↗
              </a>
              <a
                href="https://dashmaster2k.com/docs/api/"
                target='_blank'
                rel='noreferrer'
                className='px-3 py-1.5 text-sm bg-gray-700 hover:bg-gray-600 rounded'
              >
                API docs ↗
              </a>
            </div>
            <div className='text-sm text-gray-400 mb-3'>
              Base URL: <code className='px-1 py-0.5 bg-gray-800 rounded text-gray-200 break-all'>{API_BASE_URL}</code>
            </div>
            <ul className='space-y-2 text-sm'>
              <li className='p-3 bg-gray-800 rounded font-mono'>
                <span className='inline-block w-16 text-green-400'>GET</span>
                <span className='text-gray-200'>/dashboards</span>
                <div className='ml-16 text-xs text-gray-500 font-sans mt-1'>List your dashboards.</div>
              </li>
              <li className='p-3 bg-gray-800 rounded font-mono'>
                <span className='inline-block w-16 text-green-400'>GET</span>
                <span className='text-gray-200'>/devices</span>
                <div className='ml-16 text-xs text-gray-500 font-sans mt-1'>List your devices.</div>
              </li>
              <li className='p-3 bg-gray-800 rounded font-mono'>
                <span className='inline-block w-16 text-green-400'>GET</span>
                <span className='text-gray-200'>/devices/:id</span>
                <div className='ml-16 text-xs text-gray-500 font-sans mt-1'>Get a single device.</div>
              </li>
              <li className='p-3 bg-gray-800 rounded font-mono'>
                <span className='inline-block w-16 text-yellow-400'>PATCH</span>
                <span className='text-gray-200'>/devices/:id</span>
                <div className='ml-16 text-xs text-gray-500 font-sans mt-1'>
                  Update the dashboard and/or rotation of a device. Body: <code>{'{ "dashboardId": "...", "rotation": 0|90|180|270 }'}</code>
                </div>
              </li>
              <li className='p-3 bg-gray-800 rounded font-mono'>
                <span className='inline-block w-16 text-indigo-400'>POST</span>
                <span className='text-gray-200'>/devices/:id/identify</span>
                <div className='ml-16 text-xs text-gray-500 font-sans mt-1'>Flash the device's ID on its screen.</div>
              </li>
            </ul>
          </div>
        </>
      )}

      <ModalWrapper
        show={confirmRegenerate}
        heading='Regenerate API token'
        acceptButtonText='Yes, regenerate'
        onClose={() => setConfirmRegenerate(false)}
        onAccept={handleGenerate}
        loading={generating}
      >
        Your existing token will stop working immediately. Update any scripts using the old token.
      </ModalWrapper>

      <ModalWrapper
        show={confirmRevoke}
        heading='Revoke API token'
        acceptButtonText='Yes, revoke'
        onClose={() => setConfirmRevoke(false)}
        onAccept={handleRevoke}
      >
        Your API token will stop working immediately. You can generate a new one at any time.
      </ModalWrapper>
    </ProfileLayout>
  )
}
